import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  BundleRestockingType,
  BundleReturnMode,
} from "../bundle-data-constants";
import type { ReturnTogetherConfig } from "../return-together";
import { ReturnTogetherSelector } from "../return-together-constants";
import type {
  PortalSettingsPatch,
  ReturnsPortalSettingsSource,
} from "./portal-settings-source";

export const bundlesSettingsShape = {
  bundles: z.object({
    returnMode: createEnum(BundleReturnMode),
    restockingType: createEnum(BundleRestockingType),
    useShopifyNativeBundles: z.boolean(),
    returnTogetherProperties: z.array(z.string()),
  }),
};

export type BundlesSettings = z.infer<z.ZodObject<typeof bundlesSettingsShape>>;

export function bundlesSettingsFromTeam({
  settings,
}: ReturnsPortalSettingsSource): BundlesSettings {
  const { returns } = settings;
  return {
    bundles: {
      returnMode: returns?.bundleReturnMode ?? BundleReturnMode.ALWAYS_SEPARATE,
      restockingType:
        returns?.bundleRestockingType ?? BundleRestockingType.PARENT,
      useShopifyNativeBundles: returns?.useShopifyNativeBundles ?? false,
      returnTogetherProperties: (returns?.returnTogether ?? [])
        .filter(
          (config) =>
            config.selector === ReturnTogetherSelector.LINE_ITEM_PROPERTY,
        )
        .map((config) => config.value ?? ""),
    },
  };
}

/** Drops blanks and duplicates, so a half-typed row never becomes a config. */
function returnTogetherConfigs(properties: string[]): ReturnTogetherConfig[] {
  const seen = new Set<string>();
  const configs: ReturnTogetherConfig[] = [];
  for (const property of properties) {
    const trimmed = property.trim();
    if (!trimmed || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    configs.push({
      selector: ReturnTogetherSelector.LINE_ITEM_PROPERTY,
      value: trimmed,
    });
  }
  return configs;
}

export function bundlesTeamPatch({
  bundles,
}: BundlesSettings): PortalSettingsPatch {
  return {
    set: {
      "returns.bundleReturnMode": bundles.returnMode,
      "returns.bundleRestockingType": bundles.restockingType,
      "returns.useShopifyNativeBundles": bundles.useShopifyNativeBundles,
      "returns.returnTogether": returnTogetherConfigs(
        bundles.returnTogetherProperties,
      ),
    },
    unset: [],
  };
}
